import { appendAwsAttachment } from './dom/attachmentRenderer';
import { appendEvent } from './dom/eventRenderer';
import { appendMessage } from './dom/messageRenderer';
import { clearTypingIndicator, showTypingIndicator } from './dom/typingIndicator';
import { attachmentUrl } from './apiRoutes';

const CONTENT_TYPE = {
  EVENT: 'EVENT',
  MESSAGE: 'MESSAGE',
  ATTACHMENT: 'ATTACHMENT'
};

const EVENT_CONTENT_TYPE = {
  PARTICIPANT_JOINED: 'application/vnd.amazonaws.connect.event.participant.joined',
  PARTICIPANT_LEFT: 'application/vnd.amazonaws.connect.event.participant.left',
  CHAT_ENDED: 'application/vnd.amazonaws.connect.event.chat.ended',
  TYPING: 'application/vnd.amazonaws.connect.event.typing'
};

const PARTICIPANT_ROLE = {
  CUSTOMER: 'CUSTOMER',
  AGENT: 'AGENT',
  SYSTEM: 'SYSTEM'
};

const _processAwsAttachment = async (container, content, awsConnectContactId, { immediate }) => {
  if (!content.Attachments) return;

  if (content.ParticipantRole === PARTICIPANT_ROLE.CUSTOMER) {
    // AWS echoes the customer's own attachments back via WebSocket. Wait briefly to allow
    // the HTTP response to set data-message-id on the optimistically rendered message group,
    // then skip any attachment that already exists in the DOM.
    if (!immediate) await new Promise((resolve) => setTimeout(resolve, 500));
    for (const attachment of content.Attachments) {
      if (attachment.Status === 'APPROVED' && !container.querySelector(`[data-message-id="${attachment.AttachmentId}"]`)) {
        const messageGroup = appendMessage(container, 'customer', null, 'You');
        const url = attachmentUrl(awsConnectContactId, attachment.AttachmentId);
        appendAwsAttachment(messageGroup, attachment, url);
      }
    }
  } else if (content.ParticipantRole === PARTICIPANT_ROLE.AGENT) {
    const messageGroup = appendMessage(container, 'agent', null, content.DisplayName);
    content.Attachments.forEach((attachment) => {
      if (attachment.Status !== 'APPROVED') return;

      const url = attachmentUrl(awsConnectContactId, attachment.AttachmentId);
      appendAwsAttachment(messageGroup, attachment, url);
    });
  }
};

const _processAwsEvent = (container, content) => {
  if (content.ContentType === EVENT_CONTENT_TYPE.PARTICIPANT_JOINED) {
    if (content.ParticipantRole === PARTICIPANT_ROLE.CUSTOMER) {
      appendEvent(container, 'You have joined the chat');
    } else if (content.ParticipantRole === PARTICIPANT_ROLE.AGENT) {
      appendEvent(container, `${content.DisplayName} has joined the chat`);
    }
  } else if (content.ContentType === EVENT_CONTENT_TYPE.PARTICIPANT_LEFT) {
    appendEvent(container, `${content.DisplayName} has left the chat`);
  } else if (content.ContentType === EVENT_CONTENT_TYPE.CHAT_ENDED) {
    clearTypingIndicator(container);
    appendEvent(container, 'The chat has ended');
    document.dispatchEvent(new CustomEvent('chatkit.aws.chat-ended'));
  } else if (content.ContentType === EVENT_CONTENT_TYPE.TYPING && content.ParticipantRole === PARTICIPANT_ROLE.AGENT) {
    showTypingIndicator(container, content.DisplayName);
  }
};

const _processAwsMessage = async (container, content, { immediate }) => {
  clearTypingIndicator(container);

  if (content.ParticipantRole === PARTICIPANT_ROLE.SYSTEM) {
    appendEvent(container, content.Content);
  } else if (content.ParticipantRole === PARTICIPANT_ROLE.CUSTOMER) {
    // AWS echoes the customer's own messages back via WebSocket. Wait briefly to allow
    // the HTTP response to set data-message-id on the optimistically rendered message,
    // then skip rendering if that element already exists in the DOM.
    if (!immediate) await new Promise((resolve) => setTimeout(resolve, 500));
    if (container.querySelector(`[data-message-id="${content.Id}"]`)) return;
    appendMessage(container, 'customer', content.Content, 'You');
  } else if (content.ParticipantRole === PARTICIPANT_ROLE.AGENT) {
    appendMessage(container, 'agent', content.Content, content.DisplayName);
  }
};

export const processWebsocketContent = async (container, content, awsConnectContactId, { immediate = false } = {}) => {
  if (content.Type === CONTENT_TYPE.EVENT) {
    _processAwsEvent(container, content);
  } else if (content.Type === CONTENT_TYPE.MESSAGE) {
    await _processAwsMessage(container, content, { immediate });
  } else if (content.Type === CONTENT_TYPE.ATTACHMENT) {
    await _processAwsAttachment(container, content, awsConnectContactId, { immediate });
  }
};
